import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../services/api';
import { useFamily } from '../contexts/FamilyContext';
import { useAuth } from '../contexts/AuthContext';

const Recipes = () => {
  const navigate = useNavigate();
  const { currentFamily } = useFamily();
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ingredients: '',
    instructions: '',
    prep_time: '',
    prep_time_unit: 'minutes',
    cook_time: '',
    cook_time_unit: 'minutes',
    servings: '',
    image_url: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const fetchRecipes = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/recipes/?family_id=${currentFamily.id}`);
      setRecipes(response.data.results || response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [currentFamily]);

  useEffect(() => {
    if (currentFamily) {
      fetchRecipes();
    } else {
      setLoading(false);
    }
  }, [currentFamily, fetchRecipes]);

  const handleCreate = async () => {
    if (!currentFamily) {
      alert('Please select or create a family first');
      return;
    }
    
    // Validate that currentFamily has a valid ID
    if (!currentFamily.id) {
      alert('Invalid family selected. Please refresh the page or select a different family.');
      return;
    }
    
    
    try {
      let response;
      if (imageFile) {
        // Use FormData if there's an image file
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description || '');
        formDataToSend.append('ingredients', formData.ingredients);
        formDataToSend.append('instructions', formData.instructions);
        if (formData.prep_time) {
          formDataToSend.append('prep_time', formData.prep_time);
          formDataToSend.append('prep_time_unit', formData.prep_time_unit);
        }
        if (formData.cook_time) {
          formDataToSend.append('cook_time', formData.cook_time);
          formDataToSend.append('cook_time_unit', formData.cook_time_unit);
        }
        if (formData.servings) {
          formDataToSend.append('servings', formData.servings);
        }
        if (formData.image_url) {
          formDataToSend.append('image_url', formData.image_url);
        }
        formDataToSend.append('family_id', currentFamily.id);
        formDataToSend.append('image', imageFile);
        
        response = await api.post('/recipes/', formDataToSend);
      } else {
        // Prepare data for JSON request, only include time fields if they have values
        const requestData = {
          ...formData,
          family_id: currentFamily.id,
        };
        // Remove empty time fields
        if (!requestData.prep_time) {
          delete requestData.prep_time;
          delete requestData.prep_time_unit;
        }
        if (!requestData.cook_time) {
          delete requestData.cook_time;
          delete requestData.cook_time_unit;
        }
        response = await api.post('/recipes/', requestData);
      }
      
      setRecipes([response.data, ...recipes]);
      setOpen(false);
      setFormData({
        title: '',
        description: '',
        ingredients: '',
        instructions: '',
        prep_time: '',
        prep_time_unit: 'minutes',
        cook_time: '',
        cook_time_unit: 'minutes',
        servings: '',
        image_url: '',
      });
      setImageFile(null);
      setImagePreview(null);
      navigate(`/recipes/${response.data.id}`);
    } catch (error) {
      
      if (error.response?.status === 403) {
        alert('Authentication failed. Please refresh the page or log in again.');
      } else if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
      } else if (error.response?.data?.error && error.response.data.error.includes('not a member of family')) {
        alert('The selected family is no longer available. Refreshing family list...');
        // Trigger a refresh of families
        window.location.reload();
      } else {
        alert(error.response?.data?.error || error.response?.data?.detail || 'Failed to create recipe');
      }
    }
  };

  const copyShareLink = (shareToken) => {
    const url = `${window.location.origin}/recipes/shared/${shareToken}`;
    navigator.clipboard.writeText(url);
    alert('Share link copied to clipboard!');
  };

  const canEditRecipe = (recipe) => {
    if (!user || !recipe || !currentFamily) {
      return false;
    }
    const isCreator = user.uid === recipe.created_by;
    // Check if user is admin or owner by looking at their role in family members
    const userMember = currentFamily?.members?.find(m => m.user_id === user?.uid);
    const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);
    return isCreator || isAdmin;
  };

  const handleEdit = (recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      title: recipe.title || '',
      description: recipe.description || '',
      ingredients: recipe.ingredients || '',
      instructions: recipe.instructions || '',
      prep_time: recipe.prep_time || '',
      prep_time_unit: recipe.prep_time_unit || 'minutes',
      cook_time: recipe.cook_time || '',
      cook_time_unit: recipe.cook_time_unit || 'minutes',
      servings: recipe.servings || '',
      image_url: recipe.image_url || '',
    });
    setImageFile(null);
    setImagePreview(recipe.image_url_display || recipe.image_url || null);
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingRecipe) return;
    
    try {
      let response;
      if (imageFile) {
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description || '');
        formDataToSend.append('ingredients', formData.ingredients);
        formDataToSend.append('instructions', formData.instructions);
        if (formData.prep_time) {
          formDataToSend.append('prep_time', formData.prep_time);
          formDataToSend.append('prep_time_unit', formData.prep_time_unit);
        }
        if (formData.cook_time) {
          formDataToSend.append('cook_time', formData.cook_time);
          formDataToSend.append('cook_time_unit', formData.cook_time_unit);
        }
        if (formData.servings) {
          formDataToSend.append('servings', formData.servings);
        }
        if (formData.image_url) {
          formDataToSend.append('image_url', formData.image_url);
        }
        formDataToSend.append('image', imageFile);
        
        response = await api.put(`/recipes/${editingRecipe.id}/`, formDataToSend);
      } else {
        const requestData = {
          title: formData.title,
          description: formData.description || '',
          ingredients: formData.ingredients,
          instructions: formData.instructions,
          prep_time: formData.prep_time || null,
          prep_time_unit: formData.prep_time_unit,
          cook_time: formData.cook_time || null,
          cook_time_unit: formData.cook_time_unit,
          servings: formData.servings || null,
          image_url: formData.image_url || '',
        };
        
        response = await api.put(`/recipes/${editingRecipe.id}/`, requestData);
      }
      
      setRecipes(recipes.map(r => r.id === editingRecipe.id ? response.data : r));
      setEditOpen(false);
      setEditingRecipe(null);
      setFormData({
        title: '',
        description: '',
        ingredients: '',
        instructions: '',
        prep_time: '',
        prep_time_unit: 'minutes',
        cook_time: '',
        cook_time_unit: 'minutes',
        servings: '',
        image_url: '',
      });
      setImageFile(null);
      setImagePreview(null);
      alert('Recipe updated successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update recipe');
    }
  };

  const handleDeleteClick = (recipe) => {
    setRecipeToDelete(recipe);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recipeToDelete) return;
    
    try {
      await api.delete(`/recipes/${recipeToDelete.id}/`);
      setRecipes(recipes.filter(r => r.id !== recipeToDelete.id));
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
      alert('Recipe deleted successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete recipe');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Recipes</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Share Recipe
        </Button>
      </Box>

      {!currentFamily ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Family Selected
            </Typography>
            <Typography>
              Please create or join a family to start sharing recipes.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/families')}
            >
              Go to Families
            </Button>
          </CardContent>
        </Card>
      ) : loading ? (
        <Typography>Loading...</Typography>
      ) : recipes.length === 0 ? (
        <Card>
          <CardContent>
            <Typography>No recipes yet. Share your first recipe!</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {recipes.map((recipe) => (
            <Grid item xs={12} sm={6} md={4} key={recipe.id}>
              <Card>
                {(recipe.image_url_display || recipe.image_url) && (
                  <Box
                    component="img"
                    src={recipe.image_url_display || recipe.image_url}
                    alt={recipe.title}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                    }}
                  />
                )}
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {recipe.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        by {recipe.created_by_name}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={0.5}>
                      {canEditRecipe(recipe) && (
                        <>
                          <Tooltip title="Edit recipe">
                            <IconButton
                              onClick={() => handleEdit(recipe)}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete recipe">
                            <IconButton
                              onClick={() => handleDeleteClick(recipe)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Copy share link">
                        <IconButton
                          onClick={() => copyShareLink(recipe.share_token)}
                          size="small"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    onClick={() => navigate(`/recipes/${recipe.id}`)}
                  >
                    View Recipe
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => {
        setOpen(false);
        setImageFile(null);
        setImagePreview(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>Share a Recipe</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Recipe Title"
            margin="normal"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Ingredients (one per line)"
            margin="normal"
            multiline
            rows={5}
            value={formData.ingredients}
            onChange={(e) =>
              setFormData({ ...formData, ingredients: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Instructions"
            margin="normal"
            multiline
            rows={6}
            value={formData.instructions}
            onChange={(e) =>
              setFormData({ ...formData, instructions: e.target.value })
            }
          />
          <Box display="flex" gap={2} flexWrap="wrap">
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                label="Prep Time"
                type="number"
                margin="normal"
                value={formData.prep_time}
                onChange={(e) =>
                  setFormData({ ...formData, prep_time: e.target.value })
                }
                sx={{ width: 120 }}
              />
              <FormControl margin="normal" sx={{ minWidth: 100 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={formData.prep_time_unit}
                  label="Unit"
                  onChange={(e) =>
                    setFormData({ ...formData, prep_time_unit: e.target.value })
                  }
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="hours">Hours</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                label="Cook Time"
                type="number"
                margin="normal"
                value={formData.cook_time}
                onChange={(e) =>
                  setFormData({ ...formData, cook_time: e.target.value })
                }
                sx={{ width: 120 }}
              />
              <FormControl margin="normal" sx={{ minWidth: 100 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={formData.cook_time_unit}
                  label="Unit"
                  onChange={(e) =>
                    setFormData({ ...formData, cook_time_unit: e.target.value })
                  }
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="hours">Hours</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Servings"
              type="number"
              margin="normal"
              value={formData.servings}
              onChange={(e) =>
                setFormData({ ...formData, servings: e.target.value })
              }
            />
          </Box>
          <TextField
            fullWidth
            type="file"
            accept="image/*"
            label="Upload Image"
            margin="normal"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setImageFile(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                  setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
              }
            }}
            helperText="Upload an image for the recipe"
            InputLabelProps={{ shrink: true }}
          />
          {imagePreview && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <img
                src={imagePreview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
              />
            </Box>
          )}
          <TextField
            fullWidth
            label="Image URL (optional - alternative to upload)"
            margin="normal"
            value={formData.image_url}
            onChange={(e) =>
              setFormData({ ...formData, image_url: e.target.value })
            }
            helperText="Or paste an image URL instead of uploading"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpen(false);
            setImageFile(null);
            setImagePreview(null);
          }}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Share
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Recipe Dialog */}
      <Dialog open={editOpen} onClose={() => {
        setEditOpen(false);
        setEditingRecipe(null);
        setImageFile(null);
        setImagePreview(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>Edit Recipe</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Recipe Title"
            margin="normal"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Ingredients (one per line)"
            margin="normal"
            multiline
            rows={5}
            value={formData.ingredients}
            onChange={(e) =>
              setFormData({ ...formData, ingredients: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Instructions"
            margin="normal"
            multiline
            rows={6}
            value={formData.instructions}
            onChange={(e) =>
              setFormData({ ...formData, instructions: e.target.value })
            }
          />
          <Box display="flex" gap={2} flexWrap="wrap">
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                label="Prep Time"
                type="number"
                margin="normal"
                value={formData.prep_time}
                onChange={(e) =>
                  setFormData({ ...formData, prep_time: e.target.value })
                }
                sx={{ width: 120 }}
              />
              <FormControl margin="normal" sx={{ minWidth: 100 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={formData.prep_time_unit}
                  label="Unit"
                  onChange={(e) =>
                    setFormData({ ...formData, prep_time_unit: e.target.value })
                  }
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="hours">Hours</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                label="Cook Time"
                type="number"
                margin="normal"
                value={formData.cook_time}
                onChange={(e) =>
                  setFormData({ ...formData, cook_time: e.target.value })
                }
                sx={{ width: 120 }}
              />
              <FormControl margin="normal" sx={{ minWidth: 100 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={formData.cook_time_unit}
                  label="Unit"
                  onChange={(e) =>
                    setFormData({ ...formData, cook_time_unit: e.target.value })
                  }
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="hours">Hours</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Servings"
              type="number"
              margin="normal"
              value={formData.servings}
              onChange={(e) =>
                setFormData({ ...formData, servings: e.target.value })
              }
            />
          </Box>
          <TextField
            fullWidth
            type="file"
            accept="image/*"
            label="Upload New Image (optional)"
            margin="normal"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setImageFile(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                  setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
              }
            }}
            helperText="Upload a new image to replace the current one"
            InputLabelProps={{ shrink: true }}
          />
          {imagePreview && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <img
                src={imagePreview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
              />
            </Box>
          )}
          <TextField
            fullWidth
            label="Image URL (optional - alternative to upload)"
            margin="normal"
            value={formData.image_url}
            onChange={(e) =>
              setFormData({ ...formData, image_url: e.target.value })
            }
            helperText="Or paste an image URL instead of uploading"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditOpen(false);
            setEditingRecipe(null);
            setImageFile(null);
            setImagePreview(null);
          }}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">
            Update Recipe
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Recipe</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{recipeToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Recipes;
